<template>
  <div class="ai-referrer-dropdown" :class="{ disabled }" align="start">
    <div class="selector" @click.stop="toggleList">
      <p
        :class="['selected', { 'some': !isAllSelected }, {'default': !selectedAiRefererIds.size}]"
        :title="selectedAiReferrerTitle"
      >
        <template v-if="isAllSelected">{{ $t('common.all') }}</template>
        <template v-else-if="selectedAiRefererIds.size">
          <span class="selected__name">{{ selectedAiReferrerText }}</span>
          <span
            v-if="selectedAiRefererIds.size > 1"
            class="selected__count"
          >
            {{ $t('common.aiReferrerSelectedOthers', { count: selectedAiRefererIds.size - 1 }) }}
          </span>
        </template>
        <template v-else-if="!selectedAiRefererIds.size">{{ $t('common.aiReferrerPlaceholder') }}</template>
      </p>
      <i class="icon icon--drop-down size-14"></i>
    </div>
    <transition name="ui-selectbox-slide">
    <div class="ai-referrer-dropdown__list" v-show="showList" @click.stop>
      <div class="ai-referrer-dropdown__body" ref="listBody">
        <div
          class="ai-referrer-dropdown__all"
          :class="{ selected: draftIsAllSelected, disabled: selectableIds.length === 0 }"
          @click="toggleAll"
        >
          <ui-checkbox
            :value="draftIsAllSelected"
            :unselect="draftIsAllSelected || draftSelectedIds.size > 0"
            :disabled="selectableIds.length === 0"
          />
          <span>{{ $t('common.all') }}</span>
        </div>
        <div
          v-for="group in sortedData"
          :key="group.providerKey"
          class="ai-referrer-dropdown__group"
        >
          <div class="ai-referrer-dropdown__row" :class="{ disabled: !group.count }">
            <button
              type="button"
              class="ai-referrer-dropdown__expand-button"
              @click="toggleExpanded(group.providerKey)"
            >
              <i
                class="icon icon--drop-down size-12"
                :class="{ 'is-expanded': isExpanded(group.providerKey) }"
              />
            </button>
            <ui-checkbox
              :value="getGroupState(group) === 'checked'"
              :unselect="getGroupState(group) === 'indeterminate'"
              :disabled="!group.count"
              @input="toggleGroup(group)"
            />
            <span
              class="ai-referrer-dropdown__name"
              @click="toggleExpanded(group.providerKey)"
            >
              {{ group.displayName }}
            </span>
            <span class="ai-referrer-dropdown__count">
              {{ group.additionalText }}
            </span>
          </div>

          <div
            v-if="isExpanded(group.providerKey)"
            class="ai-referrer-dropdown__children"
          >
            <div
              v-for="value in group.values"
              :key="value.aiRefererId"
              class="ai-referrer-dropdown__child-row"
              :class="{ disabled: !value.count }"
              @click="toggleValue(value)"
            >
              <ui-checkbox
                :value="draftSelectedIds.has(value.aiRefererId)"
                :disabled="!value.count"
              />

              <span class="ai-referrer-dropdown__value">
                {{ value.value }}
              </span>
              <span class="ai-referrer-dropdown__count">
                {{ value.additionalText }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="ai-referrer-dropdown__footer">
        <p
          v-if="showAvailableDateNotice"
          class="ai-referrer-dropdown__notice"
          v-html="$t('common.aiReferrerAvailableDate')"
        ></p>
        <ui-button
          class="ai-referrer-dropdown__apply"
          :primary3="true"
          :size="'small'"
          :disabled="!hasSelectableItem"
          @click="applySelection"
        >
          {{ $t('common.apply') }}
        </ui-button>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import UiButton from '@/components/ui/UiButton'
import UiCheckbox from '@/components/ui/UiCheckbox'

const AI_REFERRER_AVAILABLE_DATE = '2026-05-26'

const PROVIDER_ORDER = [
  'openai_chatgpt',
  'gemini_google',
  'perplexity',
  'claude_anthropic'
]

const getProviderRank = providerKey => {
  const key = String(providerKey || '').toLowerCase()
  const orderIndex = PROVIDER_ORDER.indexOf(key)

  if (orderIndex > -1) return orderIndex
  if (key.endsWith('bot')) return PROVIDER_ORDER.length + 1

  return PROVIDER_ORDER.length
}

const sortByProviderOrder = providers => {
  return providers
    .map((provider, index) => ({ provider, index }))
    .sort((current, next) => {
      const currentDisabledRank = current.provider.count > 0 ? 0 : 1
      const nextDisabledRank = next.provider.count > 0 ? 0 : 1
      const currentRank = getProviderRank(current.provider.providerKey)
      const nextRank = getProviderRank(next.provider.providerKey)

      if (currentDisabledRank !== nextDisabledRank) return currentDisabledRank - nextDisabledRank
      if (currentRank !== nextRank) return currentRank - nextRank

      return current.index - next.index
    })
    .map(({ provider }) => provider)
}

export default {
  name: 'AiReferrerDropdown',

  components: {
    UiButton,
    UiCheckbox
  },

  props: {
    data: {
      type: Array,
      default: () => []
    },
    selectedAiRefererIds: {
      type: Set,
      default: () => new Set()
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showList: false,
      expandedGroups: new Set(),
      draftSelectedIds: new Set()
    }
  },
  watch: {
    selectedAiRefererIds: {
      immediate: true,
      handler () {
        if (!this.showList) {
          this.syncDraftSelection()
        }
      }
    }
  },
  computed: {
    ...mapState([
      'journeyPeriod'
    ]),
    showAvailableDateNotice () {
      const startDate = this.journeyPeriod && this.journeyPeriod.start

      return Boolean(startDate && startDate < AI_REFERRER_AVAILABLE_DATE)
    },
    sortedData () {
      return sortByProviderOrder(this.data)
    },
    selectableIds () {
      return this.sortedData.reduce((ids, group) => {
        return ids.concat(
          group.values
            .filter(value => value.count > 0)
            .map(value => value.aiRefererId)
        )
      }, [])
    },
    hasSelectableItem () {
      return this.selectableIds.length > 0
    },
    isAllSelected () {
      return (
        this.hasSelectableItem &&
        this.selectableIds.length === this.selectedAiRefererIds.size &&
        this.selectableIds.every(aiRefererId => this.selectedAiRefererIds.has(aiRefererId))
      )
    },
    draftIsAllSelected () {
      return (
        this.hasSelectableItem &&
        this.selectableIds.length === this.draftSelectedIds.size &&
        this.selectableIds.every(aiRefererId => this.draftSelectedIds.has(aiRefererId))
      )
    },
    selectedAiReferrerText () {
      const [firstSelectedValue] = this.selectedAiReferrerValues

      return firstSelectedValue ? firstSelectedValue.value : ''
    },
    selectedAiReferrerTitle () {
      if (!this.selectedAiRefererIds.size || this.isAllSelected) return ''

      return this.selectedAiReferrerValues
        .map(value => value.value)
        .join(', ')
    },
    selectedAiReferrerValues () {
      const selectedValues = []
      for (const group of this.sortedData) {
        group.values.forEach(value => {
          if (this.selectedAiRefererIds.has(value.aiRefererId)) {
            selectedValues.push(value)
          }
        })
      }

      return selectedValues
    }
  },
  methods: {
    syncDraftSelection () {
      this.draftSelectedIds = new Set(this.selectedAiRefererIds)
    },
    toggleList () {
      if (this.disabled) return

      if (this.showList) {
        this.closeList()
        return
      }

      this.showList = true
      this.syncDraftSelection()
      this.resetListState()
      document.addEventListener('click', this.closeList, true)
    },
    closeList (event) {
      if (event && this.$el.contains(event.target)) return

      this.showList = false
      document.removeEventListener('click', this.closeList, true)
    },
    resetListState () {
      this.expandedGroups = new Set()
      this.$nextTick(() => {
        if (this.$refs.listBody) {
          this.$refs.listBody.scrollTop = 0
        }
      })
    },
    isExpanded (groupKey) {
      return this.expandedGroups.has(groupKey)
    },

    getSelectedCount (group) {
      return group.values.filter((v) => this.draftSelectedIds.has(v.aiRefererId)).length
    },

    getSelectableValues (group) {
      return group.values.filter(value => value.count > 0)
    },

    getGroupState (group) {
      const selectableValues = this.getSelectableValues(group)
      const selectedCount = selectableValues.filter(value => {
        return this.draftSelectedIds.has(value.aiRefererId)
      }).length

      if (selectedCount === 0) return 'unchecked'
      if (selectedCount === selectableValues.length) return 'checked'

      return 'indeterminate'
    },

    toggleExpanded (groupKey) {
      const next = new Set(this.expandedGroups)

      if (next.has(groupKey)) {
        next.delete(groupKey)
      } else {
        next.add(groupKey)
      }

      this.expandedGroups = next
    },
    toggleAll () {
      if (this.selectableIds.length) {
        this.draftSelectedIds = new Set(this.draftIsAllSelected ? [] : this.selectableIds)
      }
    },
    toggleGroup (group) {
      if (group.count > 0) {
        const next = new Set(this.draftSelectedIds)
        const selectableValues = this.getSelectableValues(group)
        const isAllSelected = selectableValues.every((value) => {
          return next.has(value.aiRefererId)
        })

        if (isAllSelected) {
          selectableValues.forEach((value) => {
            next.delete(value.aiRefererId)
          })
        } else {
          selectableValues.forEach((value) => {
            next.add(value.aiRefererId)
          })
        }

        this.draftSelectedIds = next
      }
    },

    toggleValue (value) {
      if (value.count > 0) {
        const next = new Set(this.draftSelectedIds)

        if (next.has(value.aiRefererId)) {
          next.delete(value.aiRefererId)
        } else {
          next.add(value.aiRefererId)
        }

        this.draftSelectedIds = next
      }
    },
    applySelection () {
      if (!this.hasSelectableItem) return

      if (this.isSameSelection(this.selectedAiRefererIds, this.draftSelectedIds)) {
        this.closeList()
        return
      }

      this.$emit('apply', [...this.draftSelectedIds])
      this.closeList()
    },
    isSameSelection (current, next) {
      if (current.size !== next.size) return false

      return [...current].every(id => next.has(id))
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeList, true)
  }
}
</script>

<style lang="scss" scoped>
.ai-referrer-dropdown {
  position: relative;
  color: #555;
  &.disabled {
    pointer-events: none;
    opacity: 0.3;
  }
  .selector {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 142px;
    height: 34px;
    padding: 8px 35px 8px 8px;
    border-radius: 7px;
    background-color: #fff;
    box-shadow: inset 0 0 0 1px #e0e0e0;
    box-sizing: border-box;
    cursor: pointer;
    .selected {
      display: flex;
      min-width: 0;
      font-size: 13px;
      font-weight: 400;
      line-height: 1.2;
      color: #2f2f2f;
      white-space: nowrap;
      overflow: hidden;

      &__name {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &__count {
        padding-left: 2px;
        flex-shrink: 0;
      }
    }

    .icon--drop-down {
      position: absolute;
      top: 50%;
      right: 9px;
      transform: translateY(-50%);
      flex-shrink: 0;
    }
  }
  &__list {
    position: absolute;
    top: -1px;
    left: -1px;
    display: flex;
    flex-direction: column;
    width: 248px;
    max-height: 320px;
    box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: 5px;
    background-color: #fff;
    transform: translateY(0);
    opacity: 1;
    transition: max-height 0.2s ease, opacity 0.1s ease, transform 0.2s ease;
    overflow: hidden;
    z-index: 10000001;

    &.ui-selectbox-slide-enter,
    &.ui-selectbox-slide-leave-to {
      max-height: 0;
      transform: translateY(-4px);
      opacity: 0;
    }
  }

  &__body {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background: #BBB;
      border-radius: 4px;
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    &::-webkit-scrollbar-track {
      background: none;
      border-left: 4px solid transparent;
    }
  }

  &__group {
  }

  &__all {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 35px;
    padding: 0 12px;
    border-bottom: 1px solid #e0e0e0;
    box-sizing: border-box;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &.selected {
      background-color: #f4f4f4;
    }

    &.disabled {
      opacity: 0.35;
    }

    &:not(.disabled):hover {
      background-color: #ebebeb;
    }
  }

  &__row,
  &__child-row {
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      background-color: #EBEBEB;
    }
  }

  &__row {
    padding: 10px 12px;
    &.disabled {
      opacity: 0.35;

      &:hover {
        background-color: transparent;
      }
    }
  }

  &__child-row {
    padding: 4px 12px 4px 8px;
    border-radius: 12px;

    &.disabled {
      opacity: 0.35;

      &:hover {
        background-color: transparent;
      }
    }
  }

  &__footer {
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 12px;
    border-top: 1px solid #e0e0e0;
  }

  &__notice {
    flex: 1;
    margin: 0;
    color: #666;
    font-size: 11px;
    line-height: 1.4;
    word-break: keep-all;
  }

  &__apply {
    flex-shrink: 0;
  }

  /deep/ .ui_checkbox {
    margin-left: 0;
    .text {
      margin-left: 16px;
    }
    .icon--chkbox {
      width: 16px;
      height: 16px;
    }
  }
  &__expand-button {
    display: flex;
  }

  .icon--drop-down {
    transition: transform 0.2s;

    &.is-expanded {
      transform: rotate(180deg);
    }
  }

  &__name {
    flex: 1;
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  &__count {
    color: #737373;
    font-size: 12px;
  }

  &__children {
    margin: 4px 0 8px 24px;
    padding-left: 12px;
    border-left: 1px solid #e0e0e0;
  }

  &__value {
    flex: 1;
    overflow: hidden;
    color: #767676;
    font-size: 13px;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }
}
</style>
